import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { LogHelper } from "../../../core/helpers/log.helper";
import { FileDownloadService } from "../../../core/services/file-download.service";
import panzoom, { PanZoom } from 'panzoom';
import { ExpenseClaimReceiptModel } from "../../../core/models/expense.model";
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-expense-image',
  templateUrl: './expense-image.component.html',
  styleUrls: ['./expense-image.component.scss']
})
export class ExpenseImageComponent implements AfterViewInit, OnDestroy {
  @Input('thumbnailUrl') thumbnailUrl: string;
  @Input('filename') filename: string;
  @Input('enableImagesManagement') enableImagesManagement: boolean;
  @Input('receipt') receipt: ExpenseClaimReceiptModel;
  @Input('isPdf') isPdf: boolean = false;
  @Input('claimCategory') claimCategory: string;
  @Input('claimAmount') claimAmount: number;
  @Input('claimCurrency') claimCurrency: string;
  @Input('visitDate') visitDate: Date;
  @Input('visitEndDate') visitEndDate: Date;
  @Input('visitTravelDays') visitTravelDays: number;

  @Output() removeFile = new EventEmitter();

  @HostListener('document:keydown', ['$event'])
  handleTheKeyboardEvent(event: KeyboardEvent) {
    switch (event.key.toUpperCase()) {
      case "ESCAPE":
        event.preventDefault();
        this.closeZoomModal();
        break;
    }
  }

  intervalDelay: number = 4000; // 2 seconds between attempting to load an image
  currentRetryAttempt: number = 0;
  isImageProcessing = false;
  retryLimit = 5;
  interval: number;
  isLoading = false;
  imageLoadFailed = false;
  processingRequest = false;

  @ViewChild('panzoomImageWrapper', { static: false }) panzoomImageWrapper: ElementRef<HTMLDivElement>;
  panzoomInstance: PanZoom | null = null;
  rotation: number = 0;

  pdfUrl: SafeResourceUrl | null = null;

  constructor(
    private fileDownloadService: FileDownloadService,
    private renderer: Renderer2,
    private sanitizer: DomSanitizer
  ) { }

  imageUrl: string | null = null;
  showZoomModal: boolean = false;

  get dateMatchesVisit(): boolean {
    if (!this.visitDate || !this.receipt?.transactionDate) return true;
    const txn = new Date(this.receipt.transactionDate);
    txn.setHours(0, 0, 0, 0);

    const travelBuffer = this.visitTravelDays ? Math.ceil(this.visitTravelDays / 2) : 0;

    const rangeStart = new Date(this.visitDate);
    rangeStart.setHours(0, 0, 0, 0);
    rangeStart.setDate(rangeStart.getDate() - travelBuffer);

    const rangeEnd = this.visitEndDate ? new Date(this.visitEndDate) : new Date(this.visitDate);
    rangeEnd.setHours(0, 0, 0, 0);
    rangeEnd.setDate(rangeEnd.getDate() + travelBuffer);

    return txn >= rangeStart && txn <= rangeEnd;
  }

  ngAfterViewInit() {
    if (this.showZoomModal && this.panzoomImageWrapper) {
      this.initPanzoom();
    }
  }

  ngOnDestroy() {
    this.destroyPanzoom();
  }

  private initPanzoom() {
    this.destroyPanzoom();
    if (this.panzoomImageWrapper) {
      this.panzoomInstance = panzoom(this.panzoomImageWrapper.nativeElement, {
        maxZoom: 5,
        minZoom: 0.2,
        bounds: true,
        boundsPadding: 0.1,
        zoomDoubleClickSpeed: 1,
        smoothScroll: false
      });
    }
  }

  private destroyPanzoom() {
    if (this.panzoomInstance) {
      this.panzoomInstance.dispose();
      this.panzoomInstance = null;
    }
  }

  viewImage() {
    this.isLoading = true;
    this.fileDownloadService.downloadExpenseFile(this.filename).subscribe({
      next: (blob) => {
        if (this.isPdf) {
          const objectUrl = URL.createObjectURL(blob);
          this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
          this.showZoomModal = true;
          this.renderer.addClass(document.body, 'lightbox-open');
        } else {
          const reader = new FileReader();
          reader.onload = (event: any) => {
            this.imageUrl = event.target.result;
            this.showZoomModal = true;
            this.rotation = 0;
            setTimeout(() => this.initPanzoom(), 0);
            this.renderer.addClass(document.body, 'lightbox-open');
          };
          reader.readAsDataURL(blob);
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        LogHelper.log(err);
      }
    });
  }

  zoomIn() {
    if (this.panzoomInstance) {
      this.panzoomInstance.smoothZoom(this.panzoomImageWrapper.nativeElement.clientWidth / 2, this.panzoomImageWrapper.nativeElement.clientHeight / 2, 1.2);
    }
  }

  zoomOut() {
    if (this.panzoomInstance) {
      this.panzoomInstance.smoothZoom(this.panzoomImageWrapper.nativeElement.clientWidth / 2, this.panzoomImageWrapper.nativeElement.clientHeight / 2, 0.8);
    }
  }

  reset() {
    if (this.panzoomInstance) {
      this.panzoomInstance.moveTo(0, 0);
      this.panzoomInstance.zoomAbs(0, 0, 1);
      this.rotation = 0;
    }
  }

  rotateLeft() {
    this.rotation = (this.rotation - 90) % 360;
  }

  rotateRight() {
    this.rotation = (this.rotation + 90) % 360;
  }

  onImageError(event: Event) {
    if (!this.imageLoadFailed) {
      const img: HTMLImageElement = event.target as HTMLImageElement;
      this.isImageProcessing = true;

      this.interval = window.setTimeout(() => {
        img.src = img.src.split('#')[0] + `#${(new Date()).getTime()}`;
        this.currentRetryAttempt++;

        // check if retry limit has been hit
        if (this.currentRetryAttempt >= this.retryLimit) {
          LogHelper.log(`Loading image ${img.src} has failed ${this.retryLimit} or more times. No additional attempts will be made.`);
          this.isImageProcessing = false;
          this.imageLoadFailed = true;
          clearInterval(this.interval);
        }
      }, this.intervalDelay);
    }
  }

  onImageLoad(_: Event) {
    if (!this.imageLoadFailed) {
      this.isImageProcessing = false;
      clearInterval(this.interval);
    }
  }

  closeZoomModal() {
    this.showZoomModal = false;
    this.imageUrl = null;
    this.pdfUrl = null;
    this.rotation = 0;
    this.destroyPanzoom();
    this.renderer.removeClass(document.body, 'lightbox-open');
  }
}
